const DAY = 86400000;

export const parseDate = (s) => new Date(`${s}T12:00:00`);
export const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
export const addDays = (s, n) => {
  const d = parseDate(s);
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
export const daysUntil = (s) => Math.round((parseDate(s) - parseDate(todayISO())) / DAY);
export const daysBetween = (a, b) => Math.round((parseDate(b) - parseDate(a)) / DAY);

export const fmtDate = (s, opts = { month: "short", day: "numeric", year: "numeric" }) =>
  parseDate(s).toLocaleDateString("en-US", opts);
export const fmtShort = (s) => fmtDate(s, { month: "short", day: "numeric" });
export const fmtTime12 = (hhmm) => {
  if (!hhmm) return "";
  const [h, m] = String(hhmm).split(":").map(Number);
  if (Number.isNaN(h)) return hhmm;
  const ap = h < 12 ? "AM" : "PM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m || 0).padStart(2, "0")} ${ap}`;
};
export const fmtWeekday = (s) => fmtDate(s, { weekday: "short", month: "short", day: "numeric" });
export const fmtMoney = (n, cents = false) =>
  (Number(n) || 0).toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: cents ? 2 : 0, maximumFractionDigits: cents ? 2 : 0 });

// Group package pricing tiers: per-person price that varies by occupancy (number of travelers).
export const pkgTiers = (p) => {
  const raw = Array.isArray(p?.pricing) && p.pricing.length ? p.pricing : [{ travelers: 1, price: Number(p?.price) || 0 }];
  return raw.map((t) => ({ travelers: Number(t.travelers) || 1, price: Number(t.price) || 0 })).sort((a, b) => a.travelers - b.travelers);
};
export const priceForTravelers = (p, n) => {
  const tiers = pkgTiers(p);
  const exact = tiers.find((t) => t.travelers === n);
  if (exact) return exact.price;
  const below = tiers.filter((t) => t.travelers <= n);
  return (below.length ? below[below.length - 1] : tiers[0]).price;
};
export const pkgFromPrice = (p) => Math.min(...pkgTiers(p).map((t) => t.price));

export const fmtTime = (iso) => new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
export const fmtRelative = (iso) => {
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 60000;
  if (diff < 1) return "Just now";
  if (diff < 60) return `${Math.floor(diff)}m ago`;
  if (diff < 60 * 24 && d.getDate() === new Date().getDate()) return fmtTime(iso);
  if (diff < 60 * 48) return "Yesterday";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

export const returnDate = (t) => addDays(t.departure, t.nights);
// A booked trip whose departure is in the past counts as completed (no explicit COMPLETED status is ever stored).
export const isCompleted = (t) => t.status === "COMPLETED" || (t.status === "BOOKED" && daysUntil(t.departure) < 0);
export const effStatus = (t) => (isCompleted(t) ? "COMPLETED" : t.status);
export const initials = (name) => name.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();

export function lineItems(t) {
  if (Array.isArray(t.items)) return t.items;
  const n = t.travelers?.length || 0;
  if (!n || t.fare == null) return [];
  const nights = t.nights || 0;
  const vessel = t.vessel || "";
  const room = t.room || "";
  if (t.kind === "cruise") {
    return [
      { desc: `${vessel} · ${room.replace(/\s\d+$/, "")}`, category: "Cruise fare", qty: n, unit: t.fare },
      { desc: "Taxes, fees & port expenses", category: "Taxes", qty: n, unit: Math.round(t.fare * 0.12) },
      { desc: "Prepaid gratuities", category: "Gratuities", qty: n, unit: 18 * nights },
      { desc: "Travel protection plan", category: "Insurance", qty: n, unit: Math.round(t.fare * 0.07) },
      { desc: "Trip planning fee (waived)", category: "Agency", qty: 1, unit: 0 },
    ];
  }
  return [
    { desc: `${vessel} · ${room}`, category: "Accommodation", qty: nights, unit: t.fare },
    { desc: "Private airport transfers", category: "Transfers", qty: 2, unit: 180 },
    { desc: "Curated experiences package", category: "Experiences", qty: 1, unit: Math.round(t.fare * 0.9) },
    { desc: "Travel protection plan", category: "Insurance", qty: n, unit: Math.round(t.fare * 0.18) },
    { desc: "Trip planning fee (waived)", category: "Agency", qty: 1, unit: 0 },
  ];
}

export const tripTotal = (t) => lineItems(t).reduce((s, i) => s + i.qty * i.unit, 0);

export function paymentSchedule(t, extraPaid = 0) {
  const total = tripTotal(t);
  const start = t.bookedOn || todayISO();
  let rows;
  if (t.plan) {
    const dep = Math.min(total, t.plan.depositType === "percent" ? Math.round((total * t.plan.deposit) / 100) : t.plan.deposit);
    rows = [
      { id: "dep", label: "Deposit", due: t.plan.depositDue, amount: dep },
      { id: "final", label: "Final Payment", due: t.plan.finalDue, amount: total - dep },
    ];
  } else {
    const deposit = Math.round(total * 0.2);
    const second = Math.round(total * 0.4);
    rows = [
      { id: "dep", label: "Deposit", due: start, amount: deposit },
      { id: "second", label: "Second Payment", due: addDays(t.departure, -120), amount: second },
      { id: "final", label: "Final Balance", due: addDays(t.departure, -21), amount: total - deposit - second },
    ].map((r) => ({ ...r, due: r.due < start ? start : r.due }));
  }
  const baseRows = rows.slice(0, t.paidCount || 0);
  const paidAmt = baseRows.reduce((s, r) => s + r.amount, 0) + extraPaid;
  let cum = 0;
  return rows.map((r, i) => {
    cum += r.amount;
    const paid = t.status !== "QUOTE" && cum <= paidAmt + 0.01;
    const d = daysUntil(r.due);
    let state = "scheduled";
    if (t.status === "QUOTE") state = "pending";
    else if (paid) state = "paid";
    else if (d < 0) state = "overdue";
    else if (d <= 30) state = "due-soon";
    return { ...r, paid, state, paidOn: paid ? r.due : null, method: i % 2 ? "Visa •••• 4821" : "Bank transfer" };
  });
}

export const amountPaid = (t, extra = 0) => Math.min(tripTotal(t), paymentSchedule(t).filter((p) => p.paid).reduce((s, p) => s + p.amount, 0) + extra);
export const balanceDue = (t, extra = 0) => tripTotal(t) - amountPaid(t, extra);
export const nextPayment = (t, extra = 0) => (t.status === "BOOKED" ? paymentSchedule(t, extra).find((p) => !p.paid) : null);

const cruiseDay = (t, [place, detail], i, last) => {
  const city = place.split(",")[0];
  if (i === 0) return { title: `Embark in ${city}`, place, items: [["11:00", "Check in at the cruise terminal"], ["13:00", "Welcome lunch on the pool deck"], ["16:30", "Sail away & safety briefing"], ["19:30", "Dinner in the main dining room"]] };
  if (last) return { title: "Disembarkation", place, items: [["07:00", "Arrive in port"], ["07:30", "Breakfast & luggage collection"], ["09:00", "Disembark and transfer to the airport"]] };
  if (place === "At Sea") return { title: "Day at sea", place: "At Sea", items: [["09:00", "Late breakfast & spa appointments"], ["13:00", "Destination talk in the theater"], ["17:00", "Sunset at the rooftop lounge"], ["20:00", "Evening show"]] };
  return { title: city, place, items: [["08:00", "Arrive in port"], ["09:30", detail || "Explore at leisure"], ["13:00", "Lunch ashore"], ["17:30", "All aboard"], ["19:30", "Dinner on board"]] };
};

const resortDay = (t, title, i, last) => {
  if (i === 0) return { title: `Arrive at ${t.vessel}`, place: t.destination, items: [["13:00", "Private airport transfer"], ["15:00", `Check in · ${t.room}`], ["19:30", "Welcome dinner"]] };
  if (last) return { title: "Departure", place: t.destination, items: [["08:00", "Breakfast at the resort"], ["11:00", "Check out"], ["12:00", "Private transfer to the airport"]] };
  return { title, place: t.destination, items: [["08:00", "Breakfast at the resort"], ["10:00", title], ["15:00", "Afternoon at leisure"], ["19:30", "Dinner reservation"]] };
};

const genericStops = (t) =>
  Array.from({ length: t.nights + 1 }, (_, i) =>
    t.kind === "cruise" ? (i === 0 || i === t.nights ? [t.port || t.destination] : [i % 2 ? "At Sea" : `${t.destination} port of call`, "Shore excursion"]) : i === 0 ? "Arrival" : i === t.nights ? "Departure" : "Day at leisure"
  );

export function itinerary(t) {
  if (t.days) return t.days.map((d, i) => ({ ...d, day: i + 1, date: addDays(t.departure, i) }));
  const stops = t.stops?.length ? t.stops : genericStops(t);
  return stops.map((s, i) => {
    const last = i === stops.length - 1;
    const day = t.kind === "cruise" ? cruiseDay(t, s, i, last) : resortDay(t, s, i, last);
    return { ...day, day: i + 1, date: addDays(t.departure, i), items: day.items.map(([time, text]) => ({ time, text })) };
  });
}

export const commission = (t, rate) => Math.round(tripTotal(t) * (rate / 100));
export const tripCommission = (t, rate) => t.commission ?? commission(t, rate);
